import { type ReactElement, useCallback, useMemo, useState } from "react";

import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { startCase } from "lodash";
import type { PlaidAccount } from "react-plaid-link";
import { useHistory } from "react-router";

import { Badge, toastError, toastSuccess } from "@alpacahq/ui/old";
import {
  type TableColumn,
  type TableProps,
  useDataGrid,
} from "@alpacahq/ui/old/datagrid";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { queryKeys } from "~/v2/api/queryKeys";
import {
  type Billing,
  cancelStripeSubscription,
  type StripeInvoice,
  updateStripeSubscription,
} from "~/v2/api/rest/billing";
import { applyEliteMarketData, type ElitePerks } from "~/v2/api/rest/elite";
import { FeatureFlags, useFeatureEnabled } from "~/v2/helpers/featureFlags";
import { Path } from "~/v2/path";
import { getPrettyCash, getPrettyDate } from "~/v2/utils/formatting";
import { usePlaidStripeLink } from "../subscribe/review/usePlaidStripeLink";
import type { PaymentMethod } from "../subscribe/types";
import type { CancelReason } from "./presentation/CancelModal";

type PlaidStripeLinkData = {
  token: string;
  plaidAccount: PlaidAccount;
};

interface UseMarketDataOverviewControllerParams {
  accountId: string;
  billing: Billing;
  elitePerks: ElitePerks | undefined;
}

export const useMarketDataOverviewController = ({
  accountId,
  billing,
  elitePerks,
}: UseMarketDataOverviewControllerParams) => {
  const history = useHistory();

  const hasLinkedBankAccount = !!billing.bank_accounts?.length;

  const isBankAccountPaymentEnabled = useFeatureEnabled(
    FeatureFlags.MARKET_DATA_BANK_ACCOUNT_PAYMENT,
  );

  // Existing bank-funded subscribers keep the tab so they can view it / switch to
  // card. Card-funded subscribers cannot switch to the (currently deprecated) Plaid path.
  const isBankAccountEnabled =
    isBankAccountPaymentEnabled || hasLinkedBankAccount;

  // Editing/re-linking a bank account hits the deprecated backend path (500), so it
  // is only allowed once the flag is turned on after the backend fix.
  const isBankAccountEditable = isBankAccountPaymentEnabled;

  // Toggle state
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
    hasLinkedBankAccount ? "plaid" : "stripe",
  );

  // Stripe CC state
  const [isEditingStripeCC, setIsEditingStripeCC] = useState(false);
  const [stripeCCError, setStripeCCError] = useState<string | null>(null);

  // Cancel modal state
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelStep, setCancelStep] = useState<1 | 2>(1);
  const [cancelReason, setCancelReason] = useState<CancelReason | null>(null);
  const [cancelReasonText, setCancelReasonText] = useState<string>("");

  const stripe = useStripe();
  const elements = useElements();

  const [plaidStripeLinkData, setPlaidStripeLinkData] =
    useState<PlaidStripeLinkData | null>(null);

  const onPlaidStripeLinkSuccess = useCallback(
    ({
      token,
      plaidAccount,
    }: {
      token: string;
      plaidAccount: PlaidAccount;
    }) => {
      setPlaidStripeLinkData({ token, plaidAccount });
    },
    [],
  );

  const onPlaidStripeLinkError = useCallback((errorMessage: string) => {
    toastError(errorMessage);
  }, []);

  const {
    isReady: isPlaidStripeLinkReady,
    isLoading: isPlaidStripeLinkLoading,
    onOpenPlaidStripeLink,
  } = usePlaidStripeLink({
    onSuccess: onPlaidStripeLinkSuccess,
    onError: onPlaidStripeLinkError,
  });

  const queryClient = useQueryClient();

  const updatePaymentMethodMutation = useMutationRequest(
    (token: string) => updateStripeSubscription(token),
    {
      onSuccess: () => {
        toastSuccess("Payment method updated successfully");
        queryClient.invalidateQueries({
          queryKey: queryKeys.marketDataSubscription(),
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
        // Clear Plaid data after successful update
        setPlaidStripeLinkData(null);
        setIsEditingStripeCC(false);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Failed to update payment method: ${errorMessage}`);
        console.error("Failed to update payment method", error);
      },
    },
  );

  // If paying with CC we need to create a Stripe token before updating the subscription
  const createStripeTokenMutation = useMutationRequest(
    () => {
      if (!stripe) {
        throw new Error("Stripe is null");
      }
      if (!elements) {
        throw new Error("Stripe Elements is null");
      }
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error("Card Element is null");
      }
      return stripe.createToken(cardElement);
    },
    {
      onSuccess: (tokenResult) => {
        if (tokenResult.error) {
          setStripeCCError(
            tokenResult.error.message ??
              "Stripe ran into an error, please check input and try again.",
          );
          return;
        }

        if (!tokenResult.token) {
          setStripeCCError(
            "Stripe ran into an issue, please refresh page and try again.",
          );
          return;
        }

        setStripeCCError(null);

        updatePaymentMethodMutation.mutate(tokenResult.token.id);
      },
      onError: (error) => {
        setStripeCCError(
          "Stripe ran into an error, please refresh page and try again.",
        );
        console.error("createStripeTokenMutation error: ", error);
      },
    },
  );

  const onUpdatePaymentMethodClicked = () => {
    if (paymentMethod === "plaid") {
      // Edge case: button should be disabled if Plaid data does not exists
      if (!plaidStripeLinkData?.token || !plaidStripeLinkData?.plaidAccount) {
        toastError("Please link a bank account to continue");
        return;
      }

      updatePaymentMethodMutation.mutate(plaidStripeLinkData.token);
    } else if (paymentMethod === "stripe") {
      createStripeTokenMutation.mutate(true);
    }
  };

  const onStripeCcChange = () => {
    // Clear errors when user makes changes to CC input
    if (stripeCCError) {
      setStripeCCError(null);
    }
  };

  const onPaymentMethodChange = (paymentMethod: PaymentMethod) => {
    setPaymentMethod(paymentMethod);
    setIsEditingStripeCC(false);
    setPlaidStripeLinkData(null);
  };

  const cancelSubscriptionMutation = useMutationRequest(
    (reason: string) => cancelStripeSubscription(reason),
    {
      onSuccess: () => {
        toastSuccess("Subscription cancelled successfully");
        queryClient.invalidateQueries({
          queryKey: queryKeys.marketDataSubscription(),
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
        history.push(Path.ROUTE_DASHBOARD);
        setIsCancelModalOpen(false);
        setCancelStep(1);
        setCancelReason(null);
        setCancelReasonText("");
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Failed to cancel subscription: ${errorMessage}`);
        console.error("Failed to cancel subscription", error);
      },
    },
  );

  const onOpenCancelModal = () => {
    setIsCancelModalOpen(true);
    setCancelStep(1);
  };

  const onCloseCancelModal = () => {
    setIsCancelModalOpen(false);
    setCancelStep(1);
    setCancelReason(null);
    setCancelReasonText("");
  };

  const onCancelModalNextStep = () => {
    if (cancelReason) {
      setCancelStep(2);
    }
  };

  const onCancelModalPreviousStep = () => {
    setCancelStep(1);
  };

  const onConfirmSubscriptionCancel = (
    reason: CancelReason,
    reasonText?: string,
  ) => {
    const fullReason = reasonText ? `${reason}: ${reasonText}` : reason;
    cancelSubscriptionMutation.mutate(fullReason);
  };

  const switchToEliteFreeSubscriptionMutation = useMutationRequest(
    (accountId: string) => applyEliteMarketData(accountId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.marketDataSubscription(),
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.accounts.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.billing.all });
        toastSuccess(
          "You have successfully switched to the free elite subscription.",
        );
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        console.error("Failed to switch to free elite subscription", error);
        toastError(
          `Failed to switch to free elite subscription: ${errorMessage}`,
        );
      },
    },
  );

  const onSwitchToEliteFreeSubscription = () => {
    if (!accountId || !billing.plan || elitePerks?.elite_status !== "100k") {
      return;
    }
    switchToEliteFreeSubscriptionMutation.mutate(accountId);
  };

  const onSubscribeClicked = () => {
    if (!billing.plan) {
      history.push(Path.ROUTE_MARKET_DATA_SUBSCRIBE);
    }
  };

  const columns = useMemo<TableColumn<StripeInvoice>[]>(
    () => [
      {
        name: "Status",
        key: "status",
        cellRenderer: ({ status }) => (
          <>
            {status && (
              <Badge variant={status === "paid" ? "v2/emerald" : "v2/yellow"}>
                {startCase(status)}
              </Badge>
            )}
          </>
        ),
      },
      {
        name: "Invoice Number",
        key: "number",
        cellRenderer: ({ number, hosted_invoice_url }) =>
          hosted_invoice_url ? (
            <a
              href={hosted_invoice_url}
              target="_blank"
              className="font-medium font-mono text-indigo-600 text-sm underline"
              rel="noopener"
            >
              {number}
            </a>
          ) : (
            number
          ),
      },
      {
        name: "Amount",
        key: "amount_due",
        cellRenderer: ({ amount_due }) => (
          <p className="m-0 p-0 font-mono text-sm">
            {amount_due ? getPrettyCash(amount_due / 100) : "-"}
          </p>
        ),
      },
      {
        name: "Date",
        key: "created",
        cellRenderer: ({ created }) => (
          <p className="m-0 p-0 font-mono text-sm">
            {created ? getPrettyDate(created * 1000, "MM/DD/YYYY") : "-"}
          </p>
        ),
      },
    ],
    [],
  );

  const emptyMessage = useMemo<ReactElement>(
    () => (
      <div className="flex flex-col items-center justify-center gap-1 p-2">
        <p className="m-0 p-0 font-semibold text-base text-gray-400">
          Invoices
        </p>
        <p className="m-0 p-0 text-gray-400 text-sm">
          You don't have any invoices yet.
        </p>
      </div>
    ),
    [],
  );

  const rows = useMemo<StripeInvoice[]>(() => {
    return billing.stripe_invoices ?? [];
  }, [billing.stripe_invoices]);

  const tableProps: TableProps<StripeInvoice> = useDataGrid<StripeInvoice>({
    variant: "modern",
    emptyMessage,
    rows,
    columns,
  });

  const isUpdateLoading =
    updatePaymentMethodMutation.isPending ||
    createStripeTokenMutation.isPending;

  const isUpdateDisabled = useMemo(() => {
    if (paymentMethod === "plaid") {
      return !plaidStripeLinkData?.token || !plaidStripeLinkData?.plaidAccount;
    }

    // paymentMethod is Stripe CC
    return !stripe || !elements;
  }, [paymentMethod, plaidStripeLinkData, stripe, elements]);

  const onEditStripeCCClicked = () => setIsEditingStripeCC(true);

  return {
    stripe,
    elements,
    isBankAccountEnabled,
    isBankAccountEditable,
    paymentMethod,
    onPaymentMethodChange,
    isUpdateLoading,
    isUpdateDisabled,
    onUpdatePaymentMethodClicked,
    plaidStripeLink: {
      isReady: isPlaidStripeLinkReady,
      isLoading: isPlaidStripeLinkLoading,
      onOpen: onOpenPlaidStripeLink,
      token: plaidStripeLinkData?.token,
      account: plaidStripeLinkData?.plaidAccount,
    },
    isEditingStripeCC,
    onEditStripeCCClicked,
    stripeCCError,
    onStripeCcChange,
    // Cancel modal
    isCancelModalOpen,
    onOpenCancelModal,
    onCloseCancelModal,
    cancelStep,
    cancelReason,
    onCancelReasonChange: setCancelReason,
    cancelReasonText,
    onCancelReasonTextChange: setCancelReasonText,
    onCancelModalNextStep,
    onCancelModalPreviousStep,
    onConfirmSubscriptionCancel,
    isCancelingSubscription: cancelSubscriptionMutation.isPending,
    onSubscribeClicked,
    tableProps,
    onSwitchToEliteFreeSubscription,
  };
};

export type OverviewControllerReturn = ReturnType<
  typeof useMarketDataOverviewController
>;
